/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */
import { useCallback } from 'react';
import { pdfjs } from 'react-pdf';
import { useSignaturePadProvider } from '../../Provider/SignaturePadProvider';

/**
 * SignatureDrawPad Custom Hook
 * @returns {Element}
 * @constructor
 */
export function useSignatureDrawPad(fileUri) {
  // Provider properties
  const { setImagesData } = useSignaturePadProvider();

  /**
     * Determines the viewport scale factor based on the user's browser.
     *
     * This function checks the `navigator.userAgent` string to detect if the user
     * is using the Chrome browser. If Chrome is detected, a scale factor of 1.1 is returned.
     * For all other browsers, it defaults to a scale factor of 1.2.
     *
     * @returns {number} The scale factor to be applied to the viewport.
     */
  const getViewportScale = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isChromeBrowser = /chrome/i.test(userAgent);

    return isChromeBrowser ? 1.1 : 1.2;
  };

  /**
     * Converts a base64 encoded PDF to an array of PNG images.
     *
     * This function uses the PDF.js library to render each page of a base64 encoded PDF
     * to a PNG image. It creates a canvas element for rendering and iterates over each page
     * of the PDF, drawing the content onto the canvas and converting it to a data URL.
     * The resulting images, along with their dimensions, are stored in an array which is then
     * used to update the `imagesData` state.
     *
     * @param {Uint8Array | ArrayBuffer | string} data - The base64 encoded PDF data to be rendered.
     * @returns {Promise<void>} A promise that resolves when the rendering is complete.
     */
  const renderPdfToImages = useCallback(async (data) => {
    const imagesList = [];
    const canvas = document.createElement('canvas');
    canvas.className = 'canv'; // Changed to `className` for correct attribute setting
    const pdf = await pdfjs.getDocument({ data }).promise;

    /* eslint-disable no-await-in-loop */
    for (let i = 1; i <= pdf.numPages; i += 1) {
      const page = await pdf.getPage(i);

      const viewport = page.getViewport({ scale: getViewportScale() });
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: canvas.getContext('2d'), viewport,
      };

      await page.render(renderContext).promise;

      const img = canvas.toDataURL('image/png');
      imagesList[i - 1] = {
        image: img, height: canvas.height, width: canvas.width,
      };
    }

    setImagesData(imagesList);
  }, [setImagesData]);

  /**
     * Fetches a PDF file from the given URI and converts it to images.
     *
     * This function fetches a PDF file from the specified `fileUri`, reads it as a Blob,
     * and then converts the Blob to a base64 encoded string. The base64 string is then
     * decoded and passed to the `renderPdfToImages` function to be rendered as PNG images.
     *
     * @returns {Promise<void>} A promise that resolves when the PDF has been fetched and rendered.
     */
  const renderImagesFromBase64PdfData = useCallback(async () => {
    try {
      const response = await fetch(fileUri);
      const blob = await response.blob();

      // eslint-disable-next-line no-undef
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = atob(e.target.result.replace(/^data:application\/pdf;base64,/, ''));
        renderPdfToImages(data);
      };

      reader.readAsDataURL(blob);
    } catch (error) { /* Catch Error */ }
  }, [renderPdfToImages, fileUri]);

  /**
     * Converts a hexadecimal color code to an RGBA color string.
     *
     * This function takes a hexadecimal color code (e.g., `#RRGGBB`) and an optional alpha value,
     * and converts the color to an RGBA format string. The alpha value determines the opacity of
     * the color and defaults to 1 (fully opaque) if not provided.
     *
     * @param {string} hex - The hexadecimal color code (e.g., `#RRGGBB` or `#RGB`).
     * @param {number} [alpha=1] - The alpha value for the RGBA color, ranging from 0 (transparent) to 1 (opaque).
     * @returns {string} The RGBA color string.
     */
  const hex2rgba = (hex, alpha = 1) => {
    // Ensure the hex code is valid and remove the hash symbol if present
    // eslint-disable-next-line no-param-reassign
    hex = hex.replace(/^#/, '');

    // If the hex code is short (e.g., #RGB), expand it to full form (e.g., #RRGGBB)
    if (hex.length === 3) {
      // eslint-disable-next-line no-param-reassign
      hex = hex.split('').map((char) => char + char).join('');
    }

    // Extract the red, green, and blue components
    const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));

    // Return the RGBA color string
    return `rgba(${r},${g},${b},${alpha})`;
  };

  return {
    renderImagesFromBase64PdfData, hex2rgba,
  };
}
